import { useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plane, Train, Hotel, Cloud, Calendar } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { planTrip } from "@/lib/agent";

const TravelPlanner = () => {
  const { toast } = useToast();
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [travelPlan, setTravelPlan] = useState<any>(null);

  const handlePlanTrip = async () => {
    if (!userInput.trim()) {
      toast({
        title: "Missing information",
        description: "Please enter your travel request.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const plan = await planTrip(userInput);
      setTravelPlan(plan);
    } catch (error) {
      console.error("Error planning trip:", error);
      toast({
        title: "Error",
        description: "There was an error planning your trip. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="travel-container py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">AI Travel Planner</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Tell me about your travel plans and I'll help you organize everything - from transportation to activities.
          </p>
        </div>

        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex gap-4">
            <Input
              placeholder="e.g., Plan a 3-day trip to Goa from Hyderabad next weekend"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              className="text-lg"
            />
            <Button 
              onClick={handlePlanTrip}
              className="bg-travel-blue hover:bg-blue-700 min-w-[120px]"
              disabled={loading}
            >
              {loading ? "Planning..." : "Plan Trip"}
            </Button>
          </div>
        </div>

        {travelPlan && (
          <div className="max-w-6xl mx-auto">
            <Tabs defaultValue="itinerary">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="itinerary">
                  <Calendar className="w-4 h-4 mr-2" />
                  Itinerary
                </TabsTrigger>
                <TabsTrigger value="flights">
                  <Plane className="w-4 h-4 mr-2" />
                  Flights
                </TabsTrigger>
                <TabsTrigger value="trains">
                  <Train className="w-4 h-4 mr-2" />
                  Trains
                </TabsTrigger>
                <TabsTrigger value="hotels">
                  <Hotel className="w-4 h-4 mr-2" />
                  Hotels
                </TabsTrigger>
                <TabsTrigger value="weather">
                  <Cloud className="w-4 h-4 mr-2" />
                  Weather
                </TabsTrigger>
              </TabsList>

              <TabsContent value="itinerary" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Your Travel Itinerary</CardTitle>
                    <CardDescription>
                      Estimated Budget: ₹{travelPlan.budget.total.toLocaleString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-blue max-w-none">
                      <ReactMarkdown>{travelPlan.itinerary}</ReactMarkdown>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="flights" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Flight Options</CardTitle>
                    <CardDescription>
                      Estimated Cost: ₹{travelPlan.budget.transportation.toLocaleString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {travelPlan.flights?.map((flight: any, index: number) => (
                      <div key={index} className="mb-4 p-4 border rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold">{flight.itineraries[0].segments[0].carrierCode}</p>
                            <p className="text-sm text-gray-500">
                              Flight {flight.itineraries[0].segments[0].number}
                            </p>
                          </div>
                          <div className="text-xl font-bold">
                            {flight.price.total} {flight.price.currency}
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="trains" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Train Options</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {travelPlan.trains?.map((train: any, index: number) => (
                      <div key={index} className="mb-4 p-4 border rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold">{train.name}</p>
                            <p className="text-sm text-gray-500">{train.type}</p>
                          </div>
                          <div className="text-xl font-bold">₹{train.fare}</div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="hotels" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Recommended Hotels</CardTitle>
                    <CardDescription>
                      Estimated Cost: ₹{travelPlan.budget.accommodation.toLocaleString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {travelPlan.hotels?.map((hotel: any, index: number) => (
                      <div key={index} className="mb-4 p-4 border rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold">{hotel.name}</p>
                            <p className="text-sm text-gray-500">{hotel.address}</p>
                          </div>
                          <div className="text-xl font-bold">{hotel.price_range}</div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="weather" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Weather Forecast</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {travelPlan.weather && (
                      <div className="text-center">
                        <div className="text-4xl font-bold mb-2">
                          {Math.round(travelPlan.weather.main.temp)}°C
                        </div>
                        <p className="text-xl capitalize">{travelPlan.weather.weather[0].description}</p>
                        <div className="mt-4 grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-gray-500">Humidity</p>
                            <p className="font-semibold">{travelPlan.weather.main.humidity}%</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Wind Speed</p>
                            <p className="font-semibold">{travelPlan.weather.wind.speed} m/s</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default TravelPlanner; 